import type { IntelligenceReport } from '@/lib/unifiedIntelligence';

export type OperatorRankKey =
  | 'recruit'
  | 'foundation'
  | 'developing'
  | 'competent'
  | 'proficient'
  | 'expert'
  | 'elite';

export type ArchetypeKey =
  | 'iron-mule'
  | 'ghost-runner'
  | 'calisthenics-specialist'
  | 'steel-core'
  | 'consistent-grinder'
  | 'balanced-operator';

export type CommendationRarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface OperatorRank {
  key: OperatorRankKey;
  label: string;
  shortLabel: string;
  patch: string; // emoji/mark for now; can be swapped to SVG later
  minScore: number;
  maxScore: number;
  colorClass: string; // tailwind gradient
  textClass: string;
  description: string;
}

export interface OperatorArchetype {
  key: ArchetypeKey;
  label: string;
  callSign: string;
  icon: string;
  description: string;
  primaryPillar: keyof IntelligenceReport['currentState']['operatorScoreBreakdown'];
  coachingAngle: string;
}

export interface Commendation {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  rarity: CommendationRarity;
  unlocked: boolean;
  progressLabel?: string;
}

export interface Mission {
  id: string;
  title: string;
  task: string;
  xp: number;
  tag: 'strength' | 'endurance' | 'bodyweight' | 'recovery' | 'consistency' | 'nutrition' | 'comeback';
  completed?: boolean; // NEW: track if mission is complete
}

const RANKS: OperatorRank[] = [
  {
    key: 'elite',
    label: 'Tier-One Operator',
    shortLabel: 'T1',
    patch: '',
    minScore: 85,
    maxScore: 100,
    colorClass: 'from-amber-400/20 to-yellow-600/10',
    textClass: 'text-amber-300',
    description: 'Selection-ready. Sustain performance, lead from the front.',
  },
  {
    key: 'expert',
    label: 'Advanced Operator',
    shortLabel: 'ADV',
    patch: '⭐',
    minScore: 70,
    maxScore: 84,
    colorClass: 'from-purple-500/20 to-purple-700/10',
    textClass: 'text-purple-300',
    description: 'Mission-capable. Sharpen weakest pillar to break into Tier-One.',
  },
  {
    key: 'proficient',
    label: 'Proficient Operator',
    shortLabel: 'PRO',
    patch: '',
    minScore: 55,
    maxScore: 69,
    colorClass: 'from-blue-500/20 to-blue-700/10',
    textClass: 'text-blue-300',
    description: 'Solid base. Build volume and conditioning consistency.',
  },
  {
    key: 'competent',
    label: 'Combat Ready (Developing)',
    shortLabel: 'CR',
    patch: '',
    minScore: 40,
    maxScore: 54,
    colorClass: 'from-green-500/20 to-emerald-700/10',
    textClass: 'text-green-300',
    description: 'You have momentum—stack weeks and tighten execution.',
  },
  {
    key: 'developing',
    label: 'Operator Candidate',
    shortLabel: 'OC',
    patch: '',
    minScore: 25,
    maxScore: 39,
    colorClass: 'from-cyan-500/20 to-sky-700/10',
    textClass: 'text-cyan-300',
    description: 'Build capacity. Nail consistency, then push intensity.',
  },
  {
    key: 'foundation',
    label: 'Foundation Phase',
    shortLabel: 'BASE',
    patch: '',
    minScore: 10,
    maxScore: 24,
    colorClass: 'from-slate-500/20 to-slate-700/10',
    textClass: 'text-slate-300',
    description: 'Establish movement quality and training habit.',
  },
  {
    key: 'recruit',
    label: 'Recruit',
    shortLabel: 'REC',
    patch: '',
    minScore: 0,
    maxScore: 9,
    colorClass: 'from-slate-600/20 to-slate-800/10',
    textClass: 'text-slate-300',
    description: 'Start logging sessions—intel builds fast once you do.',
  },
];

export function getOperatorRank(score: number): OperatorRank {
  const s = Number.isFinite(score) ? score : 0;
  return RANKS.find((r) => s >= r.minScore) ?? RANKS[RANKS.length - 1];
}

export function getNextOperatorRank(score: number): OperatorRank | null {
  const s = Number.isFinite(score) ? score : 0;
  const above = RANKS.filter((r) => r.minScore > s).sort((a, b) => a.minScore - b.minScore);
  return above.length ? above[0] : null;
}

export function getRankProgress(score: number): { progress01: number; pointsToNext: number } {
  const rank = getOperatorRank(score);
  const next = getNextOperatorRank(score);
  if (!next) return { progress01: 1, pointsToNext: 0 };
  const span = Math.max(1, next.minScore - rank.minScore);
  const clamped = Math.min(next.minScore, Math.max(rank.minScore, score));
  const progress01 = (clamped - rank.minScore) / span;
  return { progress01, pointsToNext: Math.max(0, next.minScore - score) };
}

/**
 * Get all operator ranks for rank ladder display
 * Returns ranks sorted from lowest to highest
 */
export function getAllOperatorRanks(): OperatorRank[] {
  return [...RANKS].reverse(); // RANKS is highest-first, reverse for lowest-first
}

function strongestPillar(
  b: IntelligenceReport['currentState']['operatorScoreBreakdown']
): keyof IntelligenceReport['currentState']['operatorScoreBreakdown'] {
  const entries = Object.entries(b) as Array<[keyof typeof b, number]>;
  entries.sort((a, b2) => b2[1] - a[1]);
  return entries[0]?.[0] ?? 'strength';
}

export function getArchetype(report: IntelligenceReport): OperatorArchetype {
  const b = report.currentState.operatorScoreBreakdown;
  const primary = strongestPillar(b);
  const { recoverySpeed, rpeAccuracy } = report.patterns;

  // Archetype selection prioritizes user-perceived identity: "what am I?"
  if (primary === 'strength') {
    return {
      key: 'iron-mule',
      label: 'Iron Mule',
      callSign: 'The Pack Hauler',
      icon: '🪓',
      description: 'High force output. Built to move heavy things under stress.',
      primaryPillar: 'strength',
      coachingAngle: 'Keep strength, add endurance density to round out the profile.',
    };
  }
  if (primary === 'endurance') {
    return {
      key: 'ghost-runner',
      label: 'Ghost Runner',
      callSign: 'The Silent Engine',
      icon: '',
      description: 'Endurance-forward. You recover on the move and outlast.',
      primaryPillar: 'endurance',
      coachingAngle: 'Protect the engine: add strength anchors and joint armor.',
    };
  }
  if (primary === 'bodyweight') {
    return {
      key: 'calisthenics-specialist',
      label: 'Calisthenics Specialist',
      callSign: 'The Rope Climber',
      icon: '',
      description: 'Bodyweight mastery. High rep capacity and control.',
      primaryPillar: 'bodyweight',
      coachingAngle: 'Add lower-body strength and ruck work to become mission-complete.',
    };
  }
  if (primary === 'recovery' || recoverySpeed === 'fast') {
    return {
      key: 'steel-core',
      label: 'Steel Core',
      callSign: 'The Unbreakable',
      icon: '',
      description: 'Recovery-forward. You bounce back fast and stay operational.',
      primaryPillar: 'recovery',
      coachingAngle: 'Use your recovery advantage: add one more quality session per week.',
    };
  }
  if (primary === 'consistency') {
    return {
      key: 'consistent-grinder',
      label: 'Consistent Grinder',
      callSign: 'The Clockwork',
      icon: '⏱',
      description: 'Discipline wins. You stack sessions and build inevitability.',
      primaryPillar: 'consistency',
      coachingAngle: 'Turn consistency into domination: push intensity on key lifts.',
    };
  }

  // Fall back to a balanced archetype, with subtle flavor from RPE tendency.
  return {
    key: 'balanced-operator',
    label: 'Balanced Operator',
    callSign: rpeAccuracy === 'aggressive' ? 'The Closer' : rpeAccuracy === 'conservative' ? 'The Technician' : 'The Professional',
    icon: '',
    description: 'Well-rounded profile. No major holes, ready to specialize.',
    primaryPillar: primary,
    coachingAngle: 'Specialize your weakest pillar for the biggest score jump.',
  };
}

export function getCommendations(report: IntelligenceReport): Commendation[] {
  const { currentState, trends, predictions, confidence } = report;
  const score = currentState.operatorScore;
  const weeklySessions = currentState.weeklySessionCount;
  const readiness7d = report.trends.readiness7d
    .map((d) => d.readiness)
    .filter((v): v is number => typeof v === 'number');
  const avgReadiness7d = readiness7d.length ? readiness7d.reduce((a, b) => a + b, 0) / readiness7d.length : null;

  const list: Commendation[] = [];

  list.push({
    id: 'rank-elite',
    title: 'Tier-One Patch',
    subtitle: 'Operator Score ≥ 85',
    icon: '',
    rarity: 'legendary',
    unlocked: score >= 85,
    progressLabel: `${Math.min(85, score)}/85`,
  });

  list.push({
    id: 'mission-capable',
    title: 'Mission Capable',
    subtitle: '3+ sessions this week',
    icon: '',
    rarity: 'common',
    unlocked: weeklySessions >= 3,
    progressLabel: `${Math.min(3, weeklySessions)}/3`,
  });

  list.push({
    id: 'readiness-discipline',
    title: 'Readiness Discipline',
    subtitle: '7-day avg readiness ≥ 75',
    icon: '',
    rarity: 'rare',
    unlocked: avgReadiness7d !== null && avgReadiness7d >= 75,
    progressLabel: avgReadiness7d !== null ? `${Math.round(Math.min(75, avgReadiness7d))}/75` : undefined,
  });

  list.push({
    id: 'operator-momentum',
    title: 'Operator Momentum',
    subtitle: 'Operator Score up in last 7 days',
    icon: '',
    rarity: 'rare',
    unlocked: trends.operatorScoreDelta7d > 0,
    progressLabel: `${trends.operatorScoreDelta7d > 0 ? '+' : ''}${Math.round(trends.operatorScoreDelta7d)}`,
  });

  list.push({
    id: 'low-risk-window',
    title: 'Green Window',
    subtitle: 'Injury risk = LOW',
    icon: '',
    rarity: 'common',
    unlocked: predictions.injuryRisk.level === 'low',
  });

  list.push({
    id: 'ml-online',
    title: 'Intel Online',
    subtitle: 'ML predictions active (20+ sessions)',
    icon: '',
    rarity: 'epic',
    unlocked: confidence.hasEnoughDataForML,
    progressLabel: `${Math.min(20, confidence.sessionsAnalyzed)}/20`,
  });

  return list;
}

/**
 * Archetype focus mapping - maps archetype IDs to mission tags they should prioritize
 */
const ARCHETYPE_MISSION_PRIORITIES: Record<string, { primary: Mission['tag'], secondary: Mission['tag'] }> = {
  'pack-mule': { primary: 'endurance', secondary: 'recovery' },
  'breacher': { primary: 'strength', secondary: 'consistency' },
  'scout': { primary: 'endurance', secondary: 'bodyweight' },
  'assaulter': { primary: 'bodyweight', secondary: 'strength' },
  'operator': { primary: 'consistency', secondary: 'recovery' },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/** Pillar max scores for normalization */
const PILLAR_MAX: Record<string, number> = {
  strength: 30, endurance: 25, bodyweight: 20, recovery: 15, consistency: 10,
};

type PillarKey = keyof IntelligenceReport['currentState']['operatorScoreBreakdown'];

/**
 * Rank pillars from weakest to strongest (normalized 0-1 scale).
 * Returns array of { pillar, normalized, raw } sorted weakest-first.
 */
function rankWeaknesses(breakdown: IntelligenceReport['currentState']['operatorScoreBreakdown']): Array<{ pillar: PillarKey; normalized: number; raw: number }> {
  return (Object.keys(PILLAR_MAX) as PillarKey[])
    .map(pillar => ({
      pillar,
      raw: breakdown[pillar] ?? 0,
      normalized: (breakdown[pillar] ?? 0) / PILLAR_MAX[pillar],
    }))
    .sort((a, b) => a.normalized - b.normalized);
}

/**
 * Mulberry32 seeded PRNG. Returns a function that produces values in [0,1).
 */
function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Get an ISO-week-based seed for deterministic weekly rotation.
 */
function getWeekSeed(): number {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor((now.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);
  return now.getFullYear() * 100 + weekNumber;
}

/**
 * Fisher-Yates shuffle (seeded).
 */
function seededShuffle<T>(arr: T[], rng: () => number): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ============================================================================
// MISSION GENERATOR REGISTRY
// ============================================================================

interface MissionGenerator {
  id: string;
  tag: Mission['tag'];
  generate(report: IntelligenceReport, weekSeed: number): Mission | null;
  isAlreadyMet(report: IntelligenceReport): boolean;
}

const MISSION_GENERATORS: MissionGenerator[] = [

  // ======================== STRENGTH (6) ========================

  // Break the Plateau — targets stalled exercises
  {
    id: 'strength-plateau',
    tag: 'strength',
    isAlreadyMet: () => false, // actionable by nature
    generate(report) {
      const plateau = report.predictions.plateauWarnings[0];
      if (!plateau) return null;
      return {
        id: `strength-plateau-${plateau.exercise.toLowerCase().replace(/\s+/g, '-')}`,
        title: 'Break the Plateau',
        task: `Push past your ${plateau.exercise} stall (${plateau.weeksSinceProgress} weeks flat). Try: ${plateau.suggestedIntervention}`,
        xp: 250,
        tag: 'strength',
      };
    },
  },

  // Load Up — progress a lift that's ready
  {
    id: 'strength-load-up',
    tag: 'strength',
    isAlreadyMet: () => false,
    generate(report) {
      const ready = report.progressions.weights.ready[0];
      if (!ready) return null;
      return {
        id: `strength-load-up-${ready.exerciseName.toLowerCase().replace(/\s+/g, '-')}`,
        title: 'Load Up',
        task: `Progress ${ready.exerciseName} from ${ready.currentWeight} to ${ready.suggestedWeight} lbs this week.`,
        xp: 200,
        tag: 'strength',
      };
    },
  },

  // Tonnage Target — hit a weekly volume goal
  {
    id: 'strength-tonnage',
    tag: 'strength',
    isAlreadyMet(report) {
      const current = report.trends.weekToDate.strengthTonnageLbs;
      const target = this._getTarget(report);
      return current >= target;
    },
    _getTarget(report: IntelligenceReport): number {
      const current = report.trends.weekToDate.strengthTonnageLbs;
      // Target is 40% above current, rounded to nearest 5000
      return Math.max(10000, Math.round((current * 1.4) / 5000) * 5000);
    },
    generate(report) {
      const current = report.trends.weekToDate.strengthTonnageLbs;
      if (current <= 0) return null;
      const target = (this as any)._getTarget(report);
      return {
        id: 'strength-tonnage',
        title: 'Tonnage Target',
        task: `Hit ${target.toLocaleString()} lbs total volume this week (currently ${current.toLocaleString()}).`,
        xp: 200,
        tag: 'strength',
      };
    },
  } as MissionGenerator & { _getTarget(r: IntelligenceReport): number },

  // Raise the Bar — boost strength pillar when low
  {
    id: 'strength-pillar',
    tag: 'strength',
    isAlreadyMet(report) {
      return report.currentState.operatorScoreBreakdown.strength >= 15;
    },
    generate(report) {
      const score = report.currentState.operatorScoreBreakdown.strength;
      if (score >= 15) return null; // >50% of max
      return {
        id: 'strength-pillar',
        title: 'Raise the Bar',
        task: `Boost your Strength pillar (${score}/30). Hit 3+ compound lifts across 2 sessions.`,
        xp: 200,
        tag: 'strength',
      };
    },
  },

  // Fix the Weak Link — address under-volume muscle groups
  {
    id: 'strength-weak-link',
    tag: 'strength',
    isAlreadyMet: () => false,
    generate(report) {
      const issues = report.extended?.volumeStatus.muscleIssues;
      if (!issues?.length) return null;
      const worst = issues[0];
      return {
        id: `strength-weak-link-${worst.muscle.toLowerCase().replace(/\s+/g, '-')}`,
        title: 'Fix the Weak Link',
        task: `Add ${Math.abs(worst.adjustmentNeeded)} more sets of ${worst.muscle} training this week (currently ${worst.status}).`,
        xp: 175,
        tag: 'strength',
      };
    },
  },

  // Heavy Single — attempt a top set on a ready lift
  {
    id: 'strength-heavy',
    tag: 'strength',
    isAlreadyMet: () => false,
    generate(report) {
      const ready = report.progressions.weights.ready.find(p => p.confidence === 'high');
      if (!ready) return null;
      return {
        id: `strength-heavy-${ready.exerciseName.toLowerCase().replace(/\s+/g, '-')}`,
        title: 'Heavy Single',
        task: `Attempt a heavy set (RPE 8+) on ${ready.exerciseName} at ${ready.suggestedWeight} lbs.`,
        xp: 225,
        tag: 'strength',
      };
    },
  },

  // ======================== ENDURANCE (6) ========================

  // Step Up — increase daily step average
  {
    id: 'endurance-steps',
    tag: 'endurance',
    isAlreadyMet(report) {
      const avg = report.trends.activity?.avgSteps7d ?? 0;
      return avg >= 10000;
    },
    generate(report) {
      const avg = report.trends.activity?.avgSteps7d;
      if (avg == null || avg <= 0) return null;
      const target = Math.round((avg * 1.1) / 500) * 500; // 10% bump, rounded
      return {
        id: 'endurance-steps',
        title: 'Step Up',
        task: `Average ${target.toLocaleString()} steps/day this week (7-day avg: ${avg.toLocaleString()}).`,
        xp: 150,
        tag: 'endurance',
      };
    },
  },

  // Ruck Miles — log ruck distance
  {
    id: 'endurance-ruck',
    tag: 'endurance',
    isAlreadyMet(report) {
      return report.trends.weekToDate.ruckMiles >= 10;
    },
    generate(report) {
      const current = report.trends.weekToDate.ruckMiles;
      if (current <= 0 && !report.progressions.cardio.hasData) return null; // no ruck history
      const target = Math.max(4, Math.round((current + 3) * 10) / 10);
      return {
        id: 'endurance-ruck',
        title: 'Ruck Miles',
        task: `Log ${target} ruck miles this week (currently ${current}).`,
        xp: 200,
        tag: 'endurance',
      };
    },
  },

  // Cardio Progression — follow cardio recommendation
  {
    id: 'endurance-cardio-prog',
    tag: 'endurance',
    isAlreadyMet: () => false,
    generate(report) {
      const rec = report.progressions.cardio.recommendations[0];
      if (!rec) return null;
      return {
        id: 'endurance-cardio-prog',
        title: 'Cardio Progression',
        task: rec,
        xp: 175,
        tag: 'endurance',
      };
    },
  },

  // Build the Engine — boost endurance pillar when low
  {
    id: 'endurance-pillar',
    tag: 'endurance',
    isAlreadyMet(report) {
      return report.currentState.operatorScoreBreakdown.endurance >= 13;
    },
    generate(report) {
      const score = report.currentState.operatorScoreBreakdown.endurance;
      if (score >= 13) return null; // >50% of max
      return {
        id: 'endurance-pillar',
        title: 'Build the Engine',
        task: `Boost your Endurance pillar (${score}/25). Log 2+ cardio or ruck sessions.`,
        xp: 200,
        tag: 'endurance',
      };
    },
  },

  // VDOT Challenge — tempo run based on VDOT
  {
    id: 'endurance-vdot',
    tag: 'endurance',
    isAlreadyMet: () => false,
    generate(report) {
      const vdot = report.progressions.cardio.vdot;
      if (!vdot) return null;
      // Rough tempo pace from VDOT (simplified)
      const tempoPace = Math.round((29.54 + 5.000663 * Math.exp(-0.007546 * vdot)) * 10) / 10;
      const minutes = Math.floor(tempoPace);
      const seconds = Math.round((tempoPace - minutes) * 60).toString().padStart(2, '0');
      return {
        id: 'endurance-vdot',
        title: 'VDOT Challenge',
        task: `Your VDOT is ${vdot}. Complete a tempo run at ~${minutes}:${seconds}/mi pace.`,
        xp: 200,
        tag: 'endurance',
      };
    },
  },

  // NEAT Boost — combat activity suppression
  {
    id: 'endurance-neat',
    tag: 'endurance',
    isAlreadyMet(report) {
      return !(report.trends.activity?.suppressionDetected);
    },
    generate(report) {
      if (!report.trends.activity?.suppressionDetected) return null;
      return {
        id: 'endurance-neat',
        title: 'NEAT Boost',
        task: 'Activity suppression detected. Add a 20-min walk outside training.',
        xp: 125,
        tag: 'endurance',
      };
    },
  },

  // ======================== BODYWEIGHT (6) ========================

  // Interval Upgrade — progress a bodyweight interval
  {
    id: 'bodyweight-interval',
    tag: 'bodyweight',
    isAlreadyMet: () => false,
    generate(report) {
      const ready = report.progressions.intervals.ready[0];
      if (!ready) return null;
      return {
        id: `bodyweight-interval-${ready.exerciseName.toLowerCase().replace(/\s+/g, '-')}`,
        title: 'Interval Upgrade',
        task: `Upgrade ${ready.exerciseName}: ${ready.suggestedChange} this week.`,
        xp: 175,
        tag: 'bodyweight',
      };
    },
  },

  // Assessment Gap Closer — improve a failing assessment
  {
    id: 'bodyweight-assessment',
    tag: 'bodyweight',
    isAlreadyMet: () => false,
    generate(report) {
      const failing = report.extended?.assessmentGrades.testGrades.find(t => !t.meetsMinimum);
      if (!failing) return null;
      return {
        id: `bodyweight-assessment-${failing.testName.toLowerCase().replace(/\s+/g, '-')}`,
        title: 'Assessment Gap Closer',
        task: `Your ${failing.testName} scored ${failing.grade} (${failing.score}%). Train to close the gap to minimum standard.`,
        xp: 225,
        tag: 'bodyweight',
      };
    },
  },

  // Own Your Weight — boost bodyweight pillar when low
  {
    id: 'bodyweight-pillar',
    tag: 'bodyweight',
    isAlreadyMet(report) {
      return report.currentState.operatorScoreBreakdown.bodyweight >= 10;
    },
    generate(report) {
      const score = report.currentState.operatorScoreBreakdown.bodyweight;
      if (score >= 10) return null; // >50% of max
      return {
        id: 'bodyweight-pillar',
        title: 'Own Your Weight',
        task: `Boost your Bodyweight pillar (${score}/20). Include push-ups, pull-ups, or dips in 2+ sessions.`,
        xp: 200,
        tag: 'bodyweight',
      };
    },
  },

  // Efficiency Focus — improve worst SFR exercise
  {
    id: 'bodyweight-efficiency',
    tag: 'bodyweight',
    isAlreadyMet: () => false,
    generate(report) {
      const worst = report.extended?.exerciseEfficiency.worstExercises[0];
      if (!worst) return null;
      return {
        id: `bodyweight-efficiency-${worst.name.toLowerCase().replace(/\s+/g, '-')}`,
        title: 'Efficiency Focus',
        task: `Your ${worst.name} has low efficiency (SFR: ${worst.sfr.toFixed(1)}). Focus on quality reps with strict form.`,
        xp: 150,
        tag: 'bodyweight',
      };
    },
  },

  // Endurance Reps — always-available bodyweight fallback
  {
    id: 'bodyweight-reps',
    tag: 'bodyweight',
    isAlreadyMet: () => false,
    generate() {
      return {
        id: 'bodyweight-reps',
        title: 'Endurance Reps',
        task: 'Accumulate 150 total push-up reps across all sessions this week.',
        xp: 150,
        tag: 'bodyweight',
      };
    },
  },

  // Work Capacity Builder — improve work capacity score
  {
    id: 'bodyweight-work-capacity',
    tag: 'bodyweight',
    isAlreadyMet(report) {
      const wc = report.extended?.assessmentGrades.categoryScores?.workCapacity ?? 100;
      return wc >= 80;
    },
    generate(report) {
      const wc = report.extended?.assessmentGrades.categoryScores?.workCapacity;
      if (wc == null) return null;
      if (wc >= 80) return null;
      return {
        id: 'bodyweight-work-capacity',
        title: 'Work Capacity Builder',
        task: `Your work capacity score is ${wc}%. Add a high-rep finisher to 2 sessions.`,
        xp: 175,
        tag: 'bodyweight',
      };
    },
  },

  // ======================== RECOVERY (6) ========================

  // Tactical Deload — deload when recommended
  {
    id: 'recovery-deload',
    tag: 'recovery',
    isAlreadyMet: () => false,
    generate(report) {
      const deload = report.predictions.deloadRecommendation;
      if (!deload.needed) return null;
      return {
        id: 'recovery-deload',
        title: 'Tactical Deload',
        task: `Deload recommended (${deload.urgency} urgency). Reduce volume by 40% this week.`,
        xp: 175,
        tag: 'recovery',
      };
    },
  },

  // Risk Management — address elevated muscle ACWR
  {
    id: 'recovery-risk',
    tag: 'recovery',
    isAlreadyMet: () => false,
    generate(report) {
      const muscles = report.extended?.muscleStatus.byMuscleGroup;
      if (!muscles) return null;
      const atRisk = Object.entries(muscles).find(([, v]) => v.riskLevel === 'danger' || v.riskLevel === 'caution');
      if (!atRisk) return null;
      const [name, info] = atRisk;
      return {
        id: `recovery-risk-${name.toLowerCase().replace(/\s+/g, '-')}`,
        title: 'Risk Management',
        task: `${name} ACWR is elevated (${info.acwr.toFixed(1)}). ${info.recommendation}`,
        xp: 175,
        tag: 'recovery',
      };
    },
  },

  // Recover Harder — boost recovery pillar when low
  {
    id: 'recovery-pillar',
    tag: 'recovery',
    isAlreadyMet(report) {
      return report.currentState.operatorScoreBreakdown.recovery >= 8;
    },
    generate(report) {
      const score = report.currentState.operatorScoreBreakdown.recovery;
      if (score >= 8) return null; // >50% of max
      return {
        id: 'recovery-pillar',
        title: 'Recover Harder',
        task: `Boost your Recovery pillar (${score}/15). Take 2+ rest days and keep fatigue below 50.`,
        xp: 150,
        tag: 'recovery',
      };
    },
  },

  // Injury Prevention — when injury risk is moderate+
  {
    id: 'recovery-injury',
    tag: 'recovery',
    isAlreadyMet(report) {
      return report.predictions.injuryRisk.level === 'low';
    },
    generate(report) {
      const risk = report.predictions.injuryRisk;
      if (risk.level === 'low') return null;
      const factor = risk.factors[0] || 'elevated training load';
      return {
        id: 'recovery-injury',
        title: 'Injury Prevention',
        task: `Injury risk: ${risk.level}. Key factor: ${factor}.`,
        xp: 175,
        tag: 'recovery',
      };
    },
  },

  // Readiness Rebound — bring readiness back up
  {
    id: 'recovery-readiness',
    tag: 'recovery',
    isAlreadyMet(report) {
      return report.currentState.readiness >= 60;
    },
    generate(report) {
      const r = Math.round(report.currentState.readiness);
      if (r >= 60) return null;
      return {
        id: 'recovery-readiness',
        title: 'Readiness Rebound',
        task: `Readiness is low (${r}). Target readiness 70+ by end of week.`,
        xp: 150,
        tag: 'recovery',
      };
    },
  },

  // Sleep Discipline — improve sleep
  {
    id: 'recovery-sleep',
    tag: 'recovery',
    isAlreadyMet(report) {
      const sleep = report.extended?.biometrics?.sleepMinutes;
      return sleep != null && sleep >= 420; // 7 hours
    },
    generate(report) {
      const sleepMin = report.extended?.biometrics?.sleepMinutes;
      if (sleepMin == null) return null;
      if (sleepMin >= 420) return null;
      const hours = (sleepMin / 60).toFixed(1);
      return {
        id: 'recovery-sleep',
        title: 'Sleep Discipline',
        task: `Sleep averaging ${hours}h. Target 7+ hours for 5 nights this week.`,
        xp: 150,
        tag: 'recovery',
      };
    },
  },

  // ======================== CONSISTENCY (6) ========================

  // Session Target — hit optimal frequency
  {
    id: 'consistency-sessions',
    tag: 'consistency',
    isAlreadyMet(report) {
      const target = Math.max(3, Math.round(report.patterns.optimalFrequency));
      return report.currentState.weeklySessionCount >= target;
    },
    generate(report) {
      const target = Math.max(3, Math.round(report.patterns.optimalFrequency));
      const current = report.currentState.weeklySessionCount;
      if (current >= target) return null;
      return {
        id: 'consistency-sessions',
        title: 'Session Target',
        task: `Complete ${target} sessions this week (currently ${current}).`,
        xp: 200,
        tag: 'consistency',
      };
    },
  },

  // Program Adherence — stick with the program
  {
    id: 'consistency-program',
    tag: 'consistency',
    isAlreadyMet(report) {
      return report.currentState.programProgress >= 100;
    },
    generate(report) {
      const progress = Math.round(report.currentState.programProgress);
      const week = report.currentState.programWeek;
      if (progress >= 100 || week <= 0) return null;
      return {
        id: 'consistency-program',
        title: 'Program Adherence',
        task: `You're ${progress}% through Week ${week}. Complete this week's prescribed sessions.`,
        xp: 175,
        tag: 'consistency',
      };
    },
  },

  // Show Up — boost consistency pillar when low
  {
    id: 'consistency-pillar',
    tag: 'consistency',
    isAlreadyMet(report) {
      return report.currentState.operatorScoreBreakdown.consistency >= 5;
    },
    generate(report) {
      const score = report.currentState.operatorScoreBreakdown.consistency;
      if (score >= 5) return null; // >50% of max
      return {
        id: 'consistency-pillar',
        title: 'Show Up',
        task: `Boost your Consistency pillar (${score}/10). Log at least 3 sessions.`,
        xp: 175,
        tag: 'consistency',
      };
    },
  },

  // Outperform Last Week — beat previous week's pace
  {
    id: 'consistency-outperform',
    tag: 'consistency',
    isAlreadyMet(report) {
      return report.trends.weekToDateDelta.strengthTonnageLbs >= 0;
    },
    generate(report) {
      const delta = report.trends.weekToDateDelta.strengthTonnageLbs;
      if (delta >= 0) return null; // already ahead
      const current = report.trends.weekToDate.strengthTonnageLbs;
      const target = Math.round((current - delta) / 1000) * 1000; // what last week was
      return {
        id: 'consistency-outperform',
        title: 'Outperform Last Week',
        task: `Last week's pace was ahead by ${Math.abs(Math.round(delta)).toLocaleString()} lbs. Target ${target.toLocaleString()} lbs total.`,
        xp: 200,
        tag: 'consistency',
      };
    },
  },

  // Smart Session — adjust when performance predicted poor
  {
    id: 'consistency-smart',
    tag: 'consistency',
    isAlreadyMet(report) {
      const perf = report.predictions.nextSessionPerformance.expected;
      return perf !== 'poor' && perf !== 'below-average';
    },
    generate(report) {
      const perf = report.predictions.nextSessionPerformance;
      if (perf.expected !== 'poor' && perf.expected !== 'below-average') return null;
      return {
        id: 'consistency-smart',
        title: 'Smart Session',
        task: `Next session predicted: ${perf.expected}. Adjust intensity — quality over quantity.`,
        xp: 125,
        tag: 'consistency',
      };
    },
  },

  // Score Push — dynamic tag targeting weakest pillar
  {
    id: 'consistency-score-push',
    tag: 'consistency', // default, overridden in generate()
    isAlreadyMet(report) {
      return report.currentState.operatorScore >= 85;
    },
    generate(report) {
      const score = report.currentState.operatorScore;
      if (score >= 85) return null;
      const ranked = rankWeaknesses(report.currentState.operatorScoreBreakdown);
      const weakest = ranked[0];
      // Dynamically set tag to the weakest pillar
      const tag = weakest.pillar as Mission['tag'];
      return {
        id: 'consistency-score-push',
        title: 'Score Push',
        task: `Operator Score: ${Math.round(score)}. Weakest: ${weakest.pillar.charAt(0).toUpperCase() + weakest.pillar.slice(1)} (${weakest.raw}/${PILLAR_MAX[weakest.pillar]}). Focus training there.`,
        xp: 200,
        tag,
      };
    },
  },

  // ======================== NUTRITION (2) ========================

  // Fuel Log — restart or extend nutrition logging streak
  {
    id: 'nutrition-log-streak',
    tag: 'nutrition',
    isAlreadyMet(report) {
      const n = report.extended?.nutritionStatus;
      if (!n) return false;
      // Already met if user has logged 5+ of last 7 days
      return (n.daysTracked ?? 0) >= 5;
    },
    generate(report) {
      const n = report.extended?.nutritionStatus;
      if (!n) return null;
      const tracked = n.daysTracked ?? 0;
      const target = Math.min(7, tracked + 3);
      const isLapsed = tracked === 0;
      return {
        id: 'nutrition-log-streak',
        title: isLapsed ? 'Restart the Fuel Log' : 'Fuel Log',
        task: isLapsed
          ? 'Log meals on at least 3 days this week. Even rough estimates rebuild the data signal.'
          : `Log meals on ${target} of 7 days this week (currently ${tracked}/7).`,
        xp: isLapsed ? 175 : 150,
        tag: 'nutrition',
      };
    },
  },

  // Protein Floor — hit minimum protein per pound of bodyweight
  {
    id: 'nutrition-protein-floor',
    tag: 'nutrition',
    isAlreadyMet(report) {
      const n = report.extended?.nutritionStatus;
      if (!n) return false;
      return n.proteinAdequacy !== 'low';
    },
    generate(report) {
      const n = report.extended?.nutritionStatus;
      if (!n || n.proteinPerLbBodyweight == null) return null;
      if (n.proteinAdequacy !== 'low') return null;
      const current = n.proteinPerLbBodyweight.toFixed(2);
      return {
        id: 'nutrition-protein-floor',
        title: 'Protein Floor',
        task: `Protein at ${current} g/lb bodyweight — below the 0.8 g/lb floor for muscle retention. Hit 0.8+ on 5 days this week.`,
        xp: 175,
        tag: 'nutrition',
      };
    },
  },

  // ======================== RECOVERY (sleep + HRV — 2 added) ========================

  // Sleep Floor — average 7+ hours over the week
  {
    id: 'recovery-sleep-floor',
    tag: 'recovery',
    isAlreadyMet(report) {
      const sleepMin = report.extended?.biometrics?.sleepMinutes;
      // Met if last night was 7+ hours (rough proxy — ideally a 7d average)
      return sleepMin != null && sleepMin >= 420;
    },
    generate(report) {
      const sleepMin = report.extended?.biometrics?.sleepMinutes;
      if (sleepMin == null) return null; // no wearable data
      if (sleepMin >= 420) return null;
      const hours = (sleepMin / 60).toFixed(1);
      return {
        id: 'recovery-sleep-floor',
        title: 'Sleep Floor',
        task: `Last night: ${hours}h. Hit 7+ hours on at least 5 of the next 7 nights — sleep below 7h drops next-day output by ~10%.`,
        xp: 150,
        tag: 'recovery',
      };
    },
  },

  // HRV Trend — improve heart-rate variability vs baseline (Fitbit/Google Fit)
  {
    id: 'recovery-hrv-trend',
    tag: 'recovery',
    isAlreadyMet(report) {
      const hrv = report.extended?.biometrics;
      if (!hrv?.hrvCurrent || !hrv?.hrvBaseline) return false;
      // Met when current is at or above baseline
      return hrv.hrvCurrent >= hrv.hrvBaseline;
    },
    generate(report) {
      const hrv = report.extended?.biometrics;
      if (!hrv?.hrvCurrent || !hrv?.hrvBaseline) return null;
      if (hrv.hrvCurrent >= hrv.hrvBaseline) return null;
      const delta = Math.round(hrv.hrvBaseline - hrv.hrvCurrent);
      return {
        id: 'recovery-hrv-trend',
        title: 'Restore HRV',
        task: `HRV is ${delta}ms below your baseline — a fatigue signal. Prioritize sleep, lower training intensity 10%, and recheck in 7 days.`,
        xp: 175,
        tag: 'recovery',
      };
    },
  },

  // ======================== STRENGTH (1 added — PR proximity) ========================

  // PR Proximity — close to setting a 1RM personal record
  {
    id: 'strength-pr-proximity',
    tag: 'strength',
    isAlreadyMet: () => false,
    generate(report) {
      const ready = report.progressions.weights.ready;
      // Find the highest-confidence ready lift that's within 5% of suggested weight
      const close = ready.find(p => {
        const gap = (p.suggestedWeight - p.currentWeight) / Math.max(1, p.currentWeight);
        return p.confidence === 'high' && gap > 0 && gap <= 0.05;
      });
      if (!close) return null;
      return {
        id: `strength-pr-${close.exerciseName.toLowerCase().replace(/\s+/g, '-')}`,
        title: 'PR in Range',
        task: `${close.exerciseName} is within 5% of a new top set. Hit ${close.suggestedWeight} lbs this week — confidence is high.`,
        xp: 250,
        tag: 'strength',
      };
    },
  },

  // ======================== ASSESSMENT (1 added — retake) ========================

  // Reassess — retake the fitness assessment if it's been 4+ weeks
  {
    id: 'consistency-reassess',
    tag: 'consistency',
    isAlreadyMet: () => false, // Always actionable when generator triggers
    generate(report) {
      const a = report.extended?.assessmentGrades;
      if (!a?.hasAssessment) {
        // Never assessed — different prompt
        return {
          id: 'consistency-reassess',
          title: 'Set the Baseline',
          task: 'Take your first fitness assessment so the system can calibrate progress to YOUR numbers, not population averages.',
          xp: 200,
          tag: 'consistency',
        };
      }
      // Assessed — no way to know recency without a date field, so the
      // generator only fires when we actively need a refresh signal. For
      // now, only return on the never-assessed branch above. Future:
      // surface a `lastAssessmentAt` from extended and check 28+ days.
      return null;
    },
  },

  // ======================== COMEBACK (returning users) ========================

  // First Session Back — just train, anything counts
  {
    id: 'comeback-first-session',
    tag: 'comeback',
    isAlreadyMet(report) {
      // Met as soon as a session lands during reentry; once status flips
      // back to drifting/on-track on next compute, the mission disappears
      return report.reentry?.status !== 'returning';
    },
    generate(report) {
      if (report.reentry?.status !== 'returning') return null;
      const days = report.reentry.daysSinceLastSession;
      return {
        id: 'comeback-first-session',
        title: 'First Session Back',
        task: `${days} days off — log ONE session this week, any length, any intensity. Showing up is the metric.`,
        xp: 200,
        tag: 'comeback',
      };
    },
  },

  // Re-anchor — log a recovery score during reentry
  {
    id: 'comeback-recovery',
    tag: 'comeback',
    isAlreadyMet(report) {
      return report.reentry?.status !== 'returning' || !report.reentry?.missedRecovery;
    },
    generate(report) {
      if (report.reentry?.status !== 'returning') return null;
      if (!report.reentry?.missedRecovery) return null;
      return {
        id: 'comeback-recovery',
        title: 'Re-anchor',
        task: 'Log a recovery score today. Even rough numbers reset the readiness baseline so the workout engine has signal again.',
        xp: 100,
        tag: 'comeback',
      };
    },
  },
];

// ============================================================================
// SELECTION ALGORITHM
// ============================================================================

export function getWeeklyMissions(
  report: IntelligenceReport,
  completedMissionIds: string[] = [],
  recommendedArchetypeId?: string
): Mission[] {
  const weekSeed = getWeekSeed();
  const rng = mulberry32(weekSeed);
  const archetypePriority = recommendedArchetypeId ? ARCHETYPE_MISSION_PRIORITIES[recommendedArchetypeId] : null;
  const weaknessRanking = rankWeaknesses(report.currentState.operatorScoreBreakdown);
  const isReturning = report.reentry?.status === 'returning';

  // Generators whose targets are computed off a recent baseline (steps avg,
  // tonnage average, ruck miles run-rate). When the user has been gone 14+
  // days, those baselines are noise — skip them so the mission set isn't
  // asking for "10% more steps" when there are no recent steps.
  const BASELINE_RELATIVE_GENERATORS = new Set<string>([
    'endurance-steps',
    'endurance-ruck',
    'strength-tonnage',
    'endurance-cardio-prog',
  ]);

  // Build weakness multiplier map: weakest 3x, 2nd 2x, 3rd 1.5x, rest 1x
  const weaknessMultiplier: Record<string, number> = {};
  weaknessRanking.forEach((w, i) => {
    if (i === 0) weaknessMultiplier[w.pillar] = 3;
    else if (i === 1) weaknessMultiplier[w.pillar] = 2;
    else if (i === 2) weaknessMultiplier[w.pillar] = 1.5;
    else weaknessMultiplier[w.pillar] = 1;
  });

  // 1. Generate all candidates, filtering out already-met conditions
  type Candidate = { mission: Mission; generatorId: string; weight: number };
  const candidates: Candidate[] = [];

  for (const gen of MISSION_GENERATORS) {
    // Skip baseline-relative generators during reentry — they'd ask for "10%
    // more steps" off a noisy baseline.
    if (isReturning && BASELINE_RELATIVE_GENERATORS.has(gen.id)) continue;

    // Skip generators whose conditions are already satisfied
    if (gen.isAlreadyMet(report)) continue;

    const mission = gen.generate(report, weekSeed);
    if (!mission) continue;

    // Mark as completed if already claimed
    const isCompleted = completedMissionIds.includes(mission.id);
    mission.completed = isCompleted;

    // 2. Score each candidate
    let weight = mission.xp;

    // Weakness multiplier (use the mission's actual tag, which may be dynamic)
    weight *= (weaknessMultiplier[mission.tag] ?? 1);

    // Comeback missions dominate the slate during reentry — the user needs
    // a low-bar "log one session" prompt, not a tonnage target.
    if (isReturning && mission.tag === 'comeback') {
      weight *= 100;
    }

    // Archetype bonus
    if (archetypePriority) {
      if (mission.tag === archetypePriority.primary) weight *= 1.5;
      else if (mission.tag === archetypePriority.secondary) weight *= 1.2;
    }

    // Completed penalty — push to back but don't eliminate
    if (isCompleted) weight *= 0.01;

    candidates.push({ mission, generatorId: gen.id, weight });
  }

  // Edge case: no candidates at all
  if (candidates.length === 0) {
    return [{
      id: 'fallback-session',
      title: 'Get After It',
      task: 'Complete a training session this week.',
      xp: 100,
      tag: 'consistency',
      completed: report.currentState.weeklySessionCount >= 1,
    }];
  }

  // 3. Group by tag, shuffle within each group
  const byTag: Record<string, Candidate[]> = {};
  for (const c of candidates) {
    if (!byTag[c.mission.tag]) byTag[c.mission.tag] = [];
    byTag[c.mission.tag].push(c);
  }

  // Sort within each tag by weight descending, then shuffle ties with seeded RNG
  for (const tag of Object.keys(byTag)) {
    byTag[tag] = seededShuffle(byTag[tag], rng);
    byTag[tag].sort((a, b) => b.weight - a.weight);
  }

  // 4. Pick top 3 tags by highest candidate weight per tag
  const tagBestWeight: Array<{ tag: string; weight: number; best: Candidate }> = [];
  for (const [tag, cands] of Object.entries(byTag)) {
    if (cands.length > 0) {
      tagBestWeight.push({ tag, weight: cands[0].weight, best: cands[0] });
    }
  }
  tagBestWeight.sort((a, b) => b.weight - a.weight);

  // 5. Select best candidate from each of the top 3 tags
  const selected: Mission[] = [];
  const usedTags = new Set<string>();

  for (const entry of tagBestWeight) {
    if (selected.length >= 3) break;
    if (usedTags.has(entry.tag)) continue;
    usedTags.add(entry.tag);

    // Prefer incomplete over completed within this tag
    const tagCandidates = byTag[entry.tag];
    const incomplete = tagCandidates.find(c => !c.mission.completed);
    selected.push(incomplete ? incomplete.mission : tagCandidates[0].mission);
  }

  // If fewer than 3 tags available, fill from remaining candidates (different generators)
  if (selected.length < 3) {
    const usedIds = new Set(selected.map(m => m.id));
    const remaining = candidates
      .filter(c => !usedIds.has(c.mission.id))
      .sort((a, b) => b.weight - a.weight);
    for (const c of remaining) {
      if (selected.length >= 3) break;
      selected.push(c.mission);
    }
  }

  // 6. Sort final: incomplete first, then by XP descending
  selected.sort((a, b) => {
    if (a.completed !== b.completed) return a.completed ? 1 : -1;
    return b.xp - a.xp;
  });

  return selected;
}

/**
 * Get the current ISO week key (for weekly resets)
 */
export function getCurrentWeekKey(): string {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor((now.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);
  return `${now.getFullYear()}-W${weekNumber.toString().padStart(2, '0')}`;
}

/**
 * Get mission by ID — searches all generators, not just the top 3 returned missions
 */
export function getMissionById(report: IntelligenceReport, missionId: string): Mission | undefined {
  // First check the top 3 (fast path)
  const top3 = getWeeklyMissions(report, []);
  const found = top3.find(m => m.id === missionId);
  if (found) return found;

  // Search all generators for the mission
  const weekSeed = getWeekSeed();
  for (const gen of MISSION_GENERATORS) {
    const mission = gen.generate(report, weekSeed);
    if (mission && mission.id === missionId) return mission;
  }
  return undefined;
}
